class MainMenu extends HHBaseElement {
  EXIT_TIMEOUT = 500

  constructor() {
    super()
    this.openMenu = this.openMenu.bind(this)
    this.openMobileMenu = this.openMobileMenu.bind(this)
    this.closeMenu = this.closeMenu.bind(this)
    this.closeAllMenus = this.closeAllMenus.bind(this)
    this.handleNavBack = this.handleNavBack.bind(this)
    this.handleMenuItemEnter = this.handleMenuItemEnter.bind(this)
    this.handleMenuItemLeave = this.handleMenuItemLeave.bind(this)
    this.openMobileMenuDupe = this.openMobileMenuDupe.bind(this)
    this.closeMobileMenuDupe = this.closeMobileMenuDupe.bind(this)

    this._calculateHeaderHeight = this._calculateHeaderHeight.bind(this)
    this._calculateGRPopupLeft = this._calculateGRPopupLeft.bind(this)
    this.activeId = null
    this.hasBackDrop = true

    this.triggers = this.querySelectorAll('.js-main-navigation-link')
    this.menuItems = this.querySelectorAll('.megamenu')
    this.backdrop = this.querySelector('.js-backdrop')
    this.mobileOpenTrigger = this.querySelector('.js-mobilemenu-open-trigger')
    this.mobileMenu = document.querySelector('.js-mobilemenu')
    this.mobileCloseTrigger = this.mobileMenu?.querySelector('.js-mobilemenu-close-trigger')
    this.mobileBackMenus = this.querySelectorAll('.js-open-mobile-menu')
    this.navLeaveTimer = null
    this.announcementBar = this.querySelector(
      '#shopify-section-section-announcement-bar'
    )
    this.mobileNavbar = this.querySelector('#shopify-section-section-mobile-navbar')
    this.header = this.querySelector('#shopify-section-section-header')
    this.mobileSearch = this.querySelector('[data-mobile-search]')
    this.headerTopRow = this.querySelector('[data-header-top-row]')
    this.headerNav = this.querySelector('.js-header-nav-bottom')
    this.headerGRTrigger = this.querySelector('.js-header-gr-trigger')
    this.mql = window.matchMedia('(min-width: 992px)')
  }

  connectedCallback() {
    this._viewportWatcher()
    this._attachListeners()
    this._calculateHeaderHeight()
    this._calculateGRPopupLeft()
    this._assignFocusTraps()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  getMenuForLink(id) {
    return this.querySelector(id)
  }

  showBackdrop() {
    this.backdrop.classList[this.hasBackDrop ? 'add': 'remove']('visible')
    if (!this.mql.matches) {
      document.documentElement.classList.add('frozen')
      document.body.classList.add('frozen')
    }
  }

  hideBackdrop() {
    this.backdrop.classList.remove('visible')
    document.documentElement.classList.remove('frozen')
    document.body.classList.remove('frozen')
  }

  clearExitTimer() {
    try {
      clearTimeout(this.navLeaveTimer)
      this.navLeaveTimer = null
    } catch (error) {
      console.error(error)
    }
  }

  setExitTimer() {
    try {
      this.navLeaveTimer = setTimeout(() => {
        this.activeId = null
        this.hasBackDrop = true
        this.applyState()
      }, this.EXIT_TIMEOUT)
    } catch (error) {
      console.error(error)
    }
  }

  openMenu(event) {
    if (!this.mql.matches) {
      this.openMobileMenu()
      this.mobileOpenTrigger.classList.add('active')
    }

    try {
      const parentElement = event.currentTarget
      event.preventDefault()
      this.clearExitTimer()
      this.activeId = (parentElement.getAttribute('data-href') || parentElement.getAttribute('href') || '').split(
        '#'
      )[1]
      this.hasBackDrop = (parentElement.getAttribute('href') || '').includes('/') === false
    } catch (error) {
      this.activeId = null
      this.hasBackDrop = true
    } finally {
      this.applyState()
    }
  }

  openMobileMenu() {
    this.activeId = null
    this.hasBackDrop = true
    this.applyState()
    this.mobileOpenTrigger.classList.add('active')
    this.mobileOpenTrigger.setAttribute('aria-expanded', true)
    this.mobileMenu.classList.add('open')
    this.showBackdrop()
    setTimeout(() => {
      this.mobileCloseTrigger.focus()
      this.mobileCloseTrigger.setAttribute('aria-expanded', true)
    }, 150)
  }

  closeMenu(event) {
    event.preventDefault()
    const parentElement = event.currentTarget
    this.setExitTimer()
  }

  closeAllMenus() {
    this.activeId = null
    this.hasBackDrop = true
    this.applyState()
    this.classList.remove('is-active')
    this.mobileOpenTrigger.classList.remove('active')
    this.mobileOpenTrigger.setAttribute('aria-expanded', false)
    this.mobileCloseTrigger.setAttribute('aria-expanded', false)
    this.mobileMenu.classList.remove('open')
    this.hideBackdrop()
  }

  _assignFocusTraps() {
    for (const menuItem of this.menuItems) {
      this._setupFocusTrap(menuItem)
    }
  }

  applyState() {
    this.classList[this.isMenuActive ? 'add' : 'remove']('is-active')

    if (this.activeId) {
      this.showBackdrop()
    } else {
      if (this.mql.matches) {
        this.hideBackdrop()
      }
    }

    for (const menuItem of this.menuItems) {
      if (this.activeId === menuItem.id) {
        menuItem.open()
        setTimeout(() => {
          menuItem._firstFocusableElement.focus()
        }, 200)
      } else {
        menuItem.close()
      }
    }
  }

  handleMenuItemEnter() {
    this.clearExitTimer()
  }

  handleMenuItemLeave() {
    this.setExitTimer()
  }

  handleNavBack() {
    const triggers = this.mobileMenu.querySelectorAll('.js-main-navigation-link')
    for (const trigger of triggers) {
      const link = trigger.getAttribute('href')?.replace('#', '')
      if (link === this.activeId) {
        trigger.setAttribute('aria-expanded', false)
        break
      }
    }
    this.activeId = null
    this.hasBackDrop = true
    this.openMobileMenu()
  }

  get isMenuActive() {
    return this.mobileMenuOpen || this.activeId
  }

  get mobileMenuOpen() {
    return this.mobileMenu.classList.contains('open')
  }

  openMobileMenuDupe() {
    if (this.mobileMenuOpen) {
      this.handleNavBack()
      this.hideBackdrop()
      this.closeAllMenus()
    } else {
      this.showBackdrop()
      this.openMobileMenu()
    }
    this.applyState()
  }

  closeMobileMenuDupe() {
    if (this.mobileMenuOpen) {
      this.handleNavBack()
      this.hideBackdrop()
      this.closeAllMenus()
    } else {
      this.showBackdrop()
      this.openMobileMenu()
    }
    this.applyState()
  }

  _viewportWatcher() {
    this.mql.onchange = (e) => {
      this._removeListeners()
      this._attachListeners()
    }
  }

  _attachListeners() {
    this.addEventListener(
      'announcement-bar::ready',
      this._calculateHeaderHeight
    )
    window.addEventListener('resize', () => {
      this._calculateHeaderHeight()
      this._calculateGRPopupLeft()
    })
    this.backdrop.addEventListener('click', this.closeAllMenus)
    if (this.mql.matches) {
      for (const trigger of this.triggers) {
        trigger.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.closeMenu(event)
        })
        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.openMenu(event)
        })
        trigger.addEventListener('click', (event) => {
          const href = event.currentTarget.getAttribute('href') || '';
          if (!href.includes('#')) return;
          this.activeId = href.split(
            '#'
          )[1]
          let activeMenu = document.getElementById(this.activeId)
          if (activeMenu.classList.contains('active')) {
            this.closeMenu(event)
          } else {
            this.openMenu(event)
          }
        })
        trigger.addEventListener('pointerup', this.openMenu)
      }

      for (const menuItem of this.menuItems) {
        menuItem.addEventListener('pointerenter', this.handleMenuItemEnter)
        menuItem.addEventListener('pointerleave', this.handleMenuItemLeave)
      }
    } else {
      for (const trigger of this.triggers) {
        // check if the trigger is a link to an internal page
        const isLink = trigger.getAttribute('href').includes('/');

        if (!isLink) {
          trigger.addEventListener('click', this.openMenu)
        }
      }
      this.mobileOpenTrigger.addEventListener('click', this.openMobileMenuDupe)
      this.mobileCloseTrigger.addEventListener('click', this.closeMobileMenuDupe)

      for (const mobileBackMenu of this.mobileBackMenus) {
        mobileBackMenu.addEventListener('click', this.handleNavBack)
      }
    }
  }

  _removeListeners() {
    this.removeEventListener(
      'announcement-bar::ready',
      this._calculateHeaderHeight
    )
    window.removeEventListener('resize', this._calculateHeaderHeight)
    this.backdrop.removeEventListener('click', this.openMobileMenuDupe)
    for (const trigger of this.triggers) {
      trigger.removeEventListener('pointerenter', this.openMenu)
      trigger.removeEventListener('pointerleave', this.closeMenu)
    }

    for (const menuItem of this.menuItems) {
      menuItem.removeEventListener('pointerenter', this.handleMenuItemEnter)
      menuItem.removeEventListener('pointerleave', this.handleMenuItemLeave)
    }

    for (const mobileBackMenu of this.mobileBackMenus) {
      mobileBackMenu.removeEventListener('click', this.handleNavBack)
    }
    this.mobileOpenTrigger.removeEventListener('click', this.openMobileMenuDupe)
    this.mobileCloseTrigger.removeEventListener('click', this.closeMobileMenuDupe)
    this.backdrop.removeEventListener('pointerenter', this.closeMenu)
  }

  _calculateHeaderHeight() {
    setTimeout(() => {
      document.documentElement.style.setProperty(
        '--header-spacer',
        (this.announcementBar.offsetHeight || 0) +
          (this.header.offsetHeight || 0) +
          (this.mobileSearch?.offsetHeight || 0) +
          (this.mobileNavbar?.offsetHeight || 0) +
          'px'
      )

      document.documentElement.style.setProperty(
        '--top-row-header-spacer',
        (this.announcementBar.offsetHeight || 0) +
          (this.headerTopRow.offsetHeight || 0) +
          (this.mobileSearch.offsetHeight || 0) +
          'px'
      )

      if (this.mql.matches && this.mobileMenuOpen) {
        this.handleNavBack()
        this.hideBackdrop()
        this.closeAllMenus()
      }
      
    }, 100)
  }

  _calculateGRPopupLeft() {
    if(this.headerGRTrigger) {
      setTimeout(() => {
        document.documentElement.style.setProperty(
          '--header-gr-left',
          (this.headerNav.offsetLeft || 0) +
            (this.headerGRTrigger.offsetLeft || 0) +
              (this.headerGRTrigger.offsetWidth / 2 || 0) +
                'px'
        )
      }, 100)
    }
  }
}

window.customElements.define('main-menu', MainMenu)
